import React, { useState, useEffect, useRef } from 'react';
import { TimeInput } from '../Common/TimeInput';

const DAYS_DE = {
  monday: 'Montag',
  tuesday: 'Dienstag',
  wednesday: 'Mittwoch',
  thursday: 'Donnerstag',
  friday: 'Freitag',
  saturday: 'Samstag',
  sunday: 'Sonntag'
};

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const OpeningHoursEditor = ({
  openingHours,
  onSave,
  onChange,
  onImport,
  canImport = true,
  isEditMode = false,
  triggerRef,
}) => {
  const [hours, setHours] = useState(openingHours || []);
  const [, setSaving] = useState(false);
  const [, setMessage] = useState(null);
  const [, setValidated] = useState(false);
  const handleSaveRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setHours(openingHours || []);
    
    // Mark as validated if opening hours are loaded with data, or if in edit mode
    if (isEditMode || (openingHours && openingHours.length > 0)) {
      setValidated(true);
    } else {
      setValidated(false);
    }
  }, [openingHours, isEditMode]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    if (hours.length === 0) {
      const msg = 'Bitte fügen Sie mindestens einen Öffnungstag hinzu.';
      setMessage({ type: 'error', text: msg });
      setSaving(false);
      return msg;
    }

    if (hours.some(h => !h.opens || !h.closes)) {
      const msg = 'Bitte füllen Sie alle Öffnungs- und Schließzeiten aus.';
      setMessage({ type: 'error', text: msg });
      setSaving(false);
      return msg;
    }

    // Validierung: Öffnungszeiten müssen kleiner als Schließzeiten sein
    for (const dayHour of hours) {
      if (dayHour.opens && dayHour.closes && dayHour.opens >= dayHour.closes) {
        const msg = `${DAYS_DE[dayHour.day]}: Die Öffnungszeit muss vor der Schließzeit liegen!`;
        setMessage({ type: 'error', text: msg });
        setSaving(false);
        return msg;
      }
    }

    try {
      await onSave(hours);

      setValidated(true);
      setMessage({ type: 'success', text: 'Öffnungszeiten validiert!' });
      return null;
    } catch (error) {
      const msg = error.response?.data?.error || 'Fehler beim Validieren';
      setMessage({ type: 'error', text: msg });
      return msg;
    } finally {
      setSaving(false);
    }
  };

  // Always keep ref pointing to latest handleSave closure
  useEffect(() => { handleSaveRef.current = handleSave; });

  // Register programmatic validator on triggerRef
  useEffect(() => {
    if (triggerRef) triggerRef.current = () => handleSaveRef.current?.();
  }, [triggerRef]);

  const updateDay = (day, field, value) => {
    setValidated(false); // Reset validation when data changes
    setMessage(null);
    const existing = hours.find(h => h.day === day);
    const newHours = existing
      ? hours.map(h => h.day === day ? { ...h, [field]: value } : h)
      : [...hours, { day, opens: '', closes: '', [field]: value }];
    setHours(newHours);
    onChange?.(newHours);
  };

  const removeDay = (day) => {
    setValidated(false); // Reset validation when data changes
    setMessage(null);
    const newHours = hours.filter(h => h.day !== day);
    setHours(newHours);
    onChange?.(newHours);
  };

  const addDay = (day) => {
    setValidated(false); // Reset validation when data changes
    setMessage(null);
    if (!hours.find(h => h.day === day)) {
      const newHours = [...hours, { day, opens: '09:00', closes: '17:00' }];
      setHours(newHours);
      onChange?.(newHours);
    }
  };

  const getDayData = (day) => hours.find(h => h.day === day) || null;

  return (
    <div className="opening-hours-editor">
      <div className="opening-hours-list">
        {DAYS_ORDER.map(day => {
          const dayData = getDayData(day);
          
          return (
            <div key={day} className="opening-hours-day">
              <div className="day-header">
                <label>
                  <input
                    type="checkbox"
                    checked={!!dayData}
                    onChange={(e) => e.target.checked ? addDay(day) : removeDay(day)}
                  />
                  <span className="day-name">{DAYS_DE[day]}</span>
                </label>
              </div>
              
              {dayData && (
                <div className="day-times">
                  <span>von</span>
                  <TimeInput
                    value={dayData.opens || ''}
                    onChange={(e) => updateDay(day, 'opens', e.target.value)}
                    className="time-input text__input"
                  />
                  <span>bis</span>
                  <TimeInput
                    value={dayData.closes || ''}
                    onChange={(e) => updateDay(day, 'closes', e.target.value)}
                    className="time-input text__input"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default OpeningHoursEditor;
